/* eslint-disable complexity */
import { MoneyInput } from '@/components';
import { TableCell, TableRow } from '@/components/ui/table';
import { MoneyField, ZielstundenField } from '../../../components';
import { useTranslation } from 'react-i18next';
import { maskitoParseNumber } from '@maskito/kit';
import { calculateUmsatzZielminuten } from './calculateZielminuten';
import { useDialogContext } from '../DialogContext';
import { PrognoseUebernehmenButton } from './PrognoseUebernehmenButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { useRouteParams } from '@/domain/controlling/pages/Umsatzplanung/Planung/Dialog/useRouteParams';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useMindestbelegung } from '@/domain/controlling/hooks/useMindestbelegung/useMindestbelegung';

// eslint-disable-next-line max-lines-per-function
export function UmsatzPlanWerteTableRow() {
  const { t } = useTranslation();
  const { searchParams } = useRouteParams();
  const { datum } = searchParams;
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const mindestbelegung = useMindestbelegung(datum, standortId);
  const { planwerteForm, dispatch } = useDialogContext();

  function handleUpdateUmsatzplanwert(value: string) {
    const updatedZielminuten = calculateUmsatzZielminuten(
      maskitoParseNumber(value, ','),
      maskitoParseNumber(planwerteForm.formValues.umsatzProduktivitaet, ','),
    );
    dispatch({
      type: 'updateUmsatzPlan',
      values: {
        umsatzPlan: value,
        umsatzZielminuten: updatedZielminuten,
      },
    });
  }

  function handleUpdateUmsatzProduktivitaet(value: string) {
    const updatedZielminuten = calculateUmsatzZielminuten(
      maskitoParseNumber(planwerteForm.formValues.umsatzPlan, ','),
      maskitoParseNumber(value, ','),
    );
    dispatch({
      type: 'updateUmsatzProduktivitaet',
      values: {
        umsatzProduktivitaet: value,
        umsatzZielminuten: updatedZielminuten,
      },
    });
  }

  const isZielstundenZero =
    planwerteForm.formValues.umsatzPlan.length > 0 &&
    planwerteForm.formValues.umsatzProduktivitaet.length > 0 &&
    planwerteForm.formValues.umsatzZielminuten === 0;

  const zielstundenUnterMindestbelegung =
    planwerteForm.formValues.umsatzZielminuten &&
    planwerteForm.formValues.umsatzZielminuten < mindestbelegung;

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell className="font-medium py-4 ps-0 w-1/4">
        <MoneyInput
          id="umsatzPlan"
          value={planwerteForm.formValues.umsatzPlan}
          isIconVisible={false}
          onInput={(e) => {
            handleUpdateUmsatzplanwert(e.currentTarget.value);
          }}
          unit={t(`general.currency.EUR`)}
        />
      </TableCell>
      <TableCell className="font-medium p-4 w-1/4">
        <div className="flex items-center -ms-3 gap-1">
          <PrognoseUebernehmenButton
            type="umsatz"
            umsatzPrognose={planwerteForm.formValues.umsatzPrognose}
          />
          <MoneyField
            value={planwerteForm.formValues.umsatzPrognose}
            unit={t(`general.currency.EUR`)}
          />
        </div>
      </TableCell>
      <TableCell className="font-medium p-4 w-1/4">
        <MoneyInput
          value={planwerteForm.formValues.umsatzProduktivitaet}
          isIconVisible={false}
          onInput={(e) => {
            handleUpdateUmsatzProduktivitaet(e.currentTarget.value);
          }}
          unit={t('domain.controlling.umsatzplanung.ziele.€/Std.')}
        />
      </TableCell>
      <TableCell className="font-medium p-4 w-1/4">
        <div className="flex items-center gap-1">
          <ZielstundenField
            className={`${
              isZielstundenZero || zielstundenUnterMindestbelegung
                ? '[&>div]:text-primary-orange-O500'
                : ''
            }`}
            value={planwerteForm.formValues.umsatzZielminuten}
          />
          {zielstundenUnterMindestbelegung && (
            <FontAwesomeIcon
              className="text-primary-orange-O500"
              icon={faTriangleExclamation}
            />
          )}
          {isZielstundenZero && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <FontAwesomeIcon
                    className="text-primary-orange-O500"
                    icon={faTriangleExclamation}
                  />
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px]">
                  {t(
                    'domain.controlling.umsatzplanung.planung.dialog.umsatzKeineZielstunden',
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
